import React from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { alpha } from '@mui/material/styles';
import { selectThemeMode } from './themeSlice';
import { lightTheme, darkTheme } from '../../themes/theme';

// Mirrors the active palette onto CSS custom properties so plain stylesheets
// (App.css and third-party CSS) follow the mode instead of hardcoding colours.
const themeCssVariables = (theme) => {
    const { palette } = theme;
    return {
        ':root': {
            colorScheme: palette.mode,
            '--app-bg': palette.surface.page,
            '--app-paper': palette.background.paper,
            '--app-surface-raised': palette.surface.raised,
            '--app-surface-subtle': palette.surface.subtle,
            '--app-surface-sunken': palette.surface.sunken,
            '--app-border': palette.surface.border,
            '--app-border-strong': palette.surface.borderStrong,
            '--app-text': palette.text.primary,
            '--app-text-secondary': palette.text.secondary,
            '--app-text-disabled': palette.text.disabled,
            '--app-primary': palette.primary.main,
            '--app-primary-contrast': palette.primary.contrastText,
            '--app-error': palette.error.main,
            '--app-warning-surface': alpha(palette.warning.main, palette.mode === 'dark' ? 0.18 : 0.2),
            '--app-scrollbar-thumb': alpha(palette.text.secondary, 0.35),
            '--app-scrollbar-thumb-hover': alpha(palette.text.secondary, 0.55),
        },
        // React Flow ships a light-only stylesheet; point its own variables at
        // the active palette so the canvas and controls follow the mode.
        '.react-flow': {
            '--xy-background-color': palette.surface.page,
            '--xy-edge-stroke-default': palette.text.secondary,
            '--xy-edge-label-background-color-default': palette.surface.raised,
            '--xy-edge-label-color-default': palette.text.primary,
            '--xy-controls-button-background-color-default': palette.surface.raised,
            '--xy-controls-button-background-color-hover-default': palette.surface.subtle,
            '--xy-controls-button-color-default': palette.text.primary,
            '--xy-controls-button-color-hover-default': palette.primary.main,
            '--xy-controls-button-border-color-default': palette.surface.border,
            '--xy-attribution-background-color-default': 'transparent',
            '--xy-minimap-background-color-default': palette.surface.raised,
            backgroundColor: palette.surface.page,
        },
        '.react-flow__attribution a': { color: palette.text.disabled },
    };
};

export const ThemeProvider = ({ children }) => {
    const themeMode = useSelector(selectThemeMode);

    // Choose which theme to use based on the mode in Redux store
    const theme = themeMode === 'dark' ? darkTheme : lightTheme;

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline enableColorScheme />
            <GlobalStyles styles={themeCssVariables(theme)} />
            {children}
        </MuiThemeProvider>
    );
};
